import { Suspense, useCallback, useContext, useEffect, useMemo } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { NetworkState } from 'src/constants/network-state';
import SuspenseLoader from 'switch-fe-shared/components/Suspense/SuspenseLoader';
import { useFeatureFlag } from 'switch-fe-shared/hooks/useFeatureFlag';

import { useAuth } from '../auth';
import { useAuthBootstrap } from '../auth/hooks/useAuthBootstrap';
import { persistAuth } from '../auth/utils/persistAuth';
import {
  readSwitchUserCache,
  writeSwitchUserCache,
} from '../auth/utils/switchUserCache';
import { AbilityContext } from '../context/canContext';
import { useAppDispatch, useAppSelector } from '../hooks/hooks';
import { useApplicationGroups } from '../hooks/useApplicationGroups';
import { IUserSlice } from '../redux/user/userSlice.contracts';
import { useGetUserByIdQuery } from '../services/switch-rest-api/user.service';

export default function AuthGuard({ children }: { children: JSX.Element }) {
  const abilityRoleSlice = useAppSelector((state) => state.abilityRole);
  const isUrlNavigationEnabled = useFeatureFlag('ac_url_navigation__release');
  const {
    isSuccess: isApplicationGroupsSuccess,
    isError: isApplicationGroupsError,
  } = useApplicationGroups({
    skip: isUrlNavigationEnabled,
    syncSelection: !isUrlNavigationEnabled,
  });
  const ability = useContext(AbilityContext);

  const location = useLocation();
  const { loading, error, user, emailVerified, getTokens, logout } = useAuth();
  const cachedSwitchUser = useMemo(
    () =>
      isUrlNavigationEnabled && user?.uid
        ? readSwitchUserCache(user.uid)
        : null,
    [isUrlNavigationEnabled, user?.uid]
  );
  const {
    data: switchUser,
    isLoading: isSwitchUserLoading,
    isError: isSwitchUserError,
  } = useGetUserByIdQuery(user?.uid, { skip: !user || !!cachedSwitchUser });
  const effectiveSwitchUser = switchUser ?? cachedSwitchUser;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const onAuthenticated = useCallback(
    (userAuth: IUserSlice) => {
      dispatch(persistAuth({ userAuth }));
    },
    [dispatch]
  );

  useAuthBootstrap({
    getTokens,
    hasAuthenticationError:
      !!error ||
      abilityRoleSlice.status === NetworkState.ERROR ||
      (!isUrlNavigationEnabled && isApplicationGroupsError),
    isSwitchUserError,
    isSwitchUserLoading,
    loading,
    logout,
    navigate,
    onAuthenticated,
    switchUser: effectiveSwitchUser,
    user,
  });

  useEffect(() => {
    if (!isUrlNavigationEnabled || !user?.uid || !switchUser) return;
    writeSwitchUserCache(user.uid, switchUser);
  }, [isUrlNavigationEnabled, switchUser, user?.uid]);

  if (
    loading ||
    (isSwitchUserLoading && !cachedSwitchUser) ||
    abilityRoleSlice.status !== NetworkState.SUCCESS ||
    (!isUrlNavigationEnabled && !isApplicationGroupsSuccess) ||
    !ability
  ) {
    return (
      <Suspense fallback={<SuspenseLoader />}>
        <SuspenseLoader />
      </Suspense>
    );
  }

  if (!loading && !emailVerified) {
    return (
      <Navigate to='/status/unverified' state={{ from: location }} replace />
    );
  }

  return children;
}
